/** Numbers and sentences for the results page: plain words for a business owner, not a statistician. */

const NF = new Intl.NumberFormat('en');
export const num = (n: number) => NF.format(Math.round(n));

export const pct = (x: number, digits = 1) => `${(x * 100).toFixed(digits)}%`;

/** +12.3% / -4.0% (an ASCII hyphen, so it can be searched and copied). */
export const signedPct = (x: number, digits = 1) =>
  `${x < 0 ? '-' : '+'}${Math.abs(x * 100).toFixed(digits)}%`;

/** "+8.1% to +57.0%" */
export const range = (r: [number, number] | null | undefined) =>
  r ? `${signedPct(r[0])} to ${signedPct(r[1])}` : '';

/** A probability as a person would say it: never "100%" or "0%" for something that is not certain. */
export function chance(p: number | null | undefined): string {
  if (p === null || p === undefined) return '';
  if (p >= 0.995) return 'over 99%';
  if (p < 0.005) return 'under 1%';
  return `${Math.round(p * 100)}%`;
}

export const money = (x: number) => x.toFixed(2);

export type Tier =
  'confirmed' | 'leaning' | 'too_early' | 'no_meaningful_difference' | 'check_data';

export const TIERS: Record<Tier, { label: string; tone: 'ok' | 'warn' | 'muted' | 'err' }> = {
  confirmed: { label: 'Confirmed', tone: 'ok' },
  leaning: { label: 'Leaning', tone: 'warn' },
  too_early: { label: 'Too early', tone: 'muted' },
  no_meaningful_difference: { label: 'No meaningful difference', tone: 'muted' },
  check_data: { label: 'Check your data', tone: 'err' },
};

export interface NamedVariation {
  id: string;
  name: string;
  isControl: boolean;
}

/** The one sentence at the top of the page. */
export function headlineFor(
  tier: Tier,
  winnerId: string | null,
  variations: NamedVariation[],
): string {
  const winner = variations.find((v) => v.id === winnerId);
  const control = winner?.isControl === true;
  switch (tier) {
    case 'confirmed':
      return !winner
        ? 'There is a clear result.'
        : control
          ? 'The original is better. Keep it.'
          : `${winner.name} is the winner.`;
    case 'leaning':
      return !winner
        ? 'One side looks better, but it is not proven yet.'
        : control
          ? 'The original looks better, but it is not proven yet.'
          : `${winner.name} looks better, but it is not proven yet.`;
    case 'too_early':
      return 'Too early to tell.';
    case 'no_meaningful_difference':
      return 'No difference worth acting on.';
    case 'check_data':
      return 'Something is wrong with the visitor numbers.';
  }
}

/** What the tier means for what to do next. */
export const ADVICE: Record<Tier, string> = {
  confirmed: 'You can act on this. The difference is larger than chance can plausibly explain.',
  leaning: 'Keep it running. This is a hint, not a result: do not act on it yet.',
  too_early: 'Keep it running. There is not enough data to say anything yet.',
  no_meaningful_difference:
    'Enough visitors were counted to see a worthwhile difference, and there is none. Pick whichever you prefer, or try a bolder change.',
  check_data:
    'The visitors were not split between the versions the way they should have been, so the numbers cannot be trusted until the cause is found.',
};

/** How far along the test is, in a sentence. */
export function progressText(
  progress: number,
  planned: number,
  lowestVisitors: number,
  remainingDays: number | null,
  mde: number,
): string {
  if (!planned) return 'Waiting for the first visitors.';
  const done = progress >= 1;
  const base = done
    ? `The planned sample is complete: ${num(lowestVisitors)} visitors in the smallest variation (about ${num(planned)} are needed to see a ${Math.round(mde * 100)}% improvement).`
    : `${num(lowestVisitors)} of about ${num(planned)} visitors per variation (${Math.round(progress * 100)}%) needed to see a ${Math.round(mde * 100)}% improvement.`;
  if (done) return base;
  if (remainingDays === null)
    return `${base} There is not enough traffic yet to estimate how long that will take.`;
  if (remainingDays <= 1) return `${base} Roughly a day more at the current traffic.`;
  const weeks = remainingDays > 21 ? ` (about ${Math.round(remainingDays / 7)} weeks)` : '';
  return `${base} About ${num(remainingDays)} more days at the current traffic${weeks}.`;
}
